import { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Search, Save, Shield, User } from 'lucide-react';
import { AreaRoleSyncPanel } from '@/components/admin/AreaRoleSyncPanel';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Función de `t`: los labels salen de i18n y `t` no existe a nivel de módulo.
// El `as const` conserva los ids literales para el tipo TabId.
type TFunction = ReturnType<typeof useTranslation>['t'];
const TAB_OPTIONS = (t: TFunction) => [
  { id: 'tab:inventory', label: t('admin.userpermissions.inventario_dispositivos'), description: t('admin.userpermissions.inventario_aprobaciones_info_util') },
  { id: 'tab:approvals', label: t('admin.userpermissions.aprobaciones_de_compra'), description: t('admin.userpermissions.aprobar_rechazar_solicitudes') },
  { id: 'tab:users', label: t('admin.userpermissions.gestion_de_usuarios'), description: t('admin.userpermissions.alta_baja_inactivos_community_map') },
  { id: 'tab:performance', label: 'Performance', description: t('admin.userpermissions.evaluaciones_de_performance_flavia') },
  { id: 'subtab:performance:admin', label: t('admin.userpermissions.performance_admin'), description: t('admin.userpermissions.acceso_al_panel_de_administracion_de') },
  { id: 'tab:ia-native', label: t('admin.userpermissions.ia_native'), description: t('admin.userpermissions.dashboard_y_gestion_del_programa_ia') },
  { id: 'tab:finops', label: 'FinOps', description: t('admin.userpermissions.costos_de_cloud_y_herramientas_technology') },
  { id: 'subtab:internal:adoption', label: t('admin.userpermissions.internal_adoption'), description: t('admin.userpermissions.internal_adoption_desc') },
] as const;

type TabId = ReturnType<typeof TAB_OPTIONS>[number]['id'];

export function UserPermissionsPage() {
  const { t } = useTranslation();
  const [searchEmail, setSearchEmail] = useState('');
  const [queriedEmail, setQueriedEmail] = useState('');
  const [selectedTabs, setSelectedTabs] = useState<TabId[]>([]);

  const { data: userPerms, isLoading: isLoadingPerms, refetch } = trpc.extraPermissions.getByEmail.useQuery(
    { email: queriedEmail },
    { enabled: !!queriedEmail }
  );

  useEffect(() => {
    if (userPerms) {
      setSelectedTabs((userPerms.tabs ?? []) as TabId[]);
    }
  }, [userPerms]);

  const { data: allUsers, isLoading: isLoadingAll } = trpc.extraPermissions.listAll.useQuery();

  const setMutation = trpc.extraPermissions.set.useMutation({
    onSuccess: () => {
      toast.success(t('admin.permissions.permissions_saved', { email: queriedEmail }));
      refetch();
    },
    onError: (err) => {
      toast.error(t('admin.permissions.save_error', { message: err.message }));
    },
  });

  const handleSearch = () => {
    const trimmed = searchEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error(t('admin.permissions.invalid_email_error'));
      return;
    }
    setQueriedEmail(trimmed);
    setSelectedTabs([]);
  };

  const toggleTab = (tabId: TabId) => {
    setSelectedTabs((prev) =>
      prev.includes(tabId) ? prev.filter((id) => id !== tabId) : [...prev, tabId]
    );
  };

  const handleSave = () => {
    if (!queriedEmail) return;
    setMutation.mutate({ email: queriedEmail, tabs: selectedTabs });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          {t('admin.permissions.page_title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('admin.permissions.page_subtitle')}
        </p>
      </div>

      <AreaRoleSyncPanel />

      {/* Search */}
      <div className="rounded-lg border bg-card p-6 space-y-4">
        <h2 className="font-semibold">{t('admin.permissions.search_section_title')}</h2>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder={t('admin.permissions.search_placeholder')}
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleSearch}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Search className="h-4 w-4" />
            {t('admin.permissions.search_btn')}
          </button>
        </div>

        {/* User perms editor */}
        {queriedEmail && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{queriedEmail}</span>
              {userPerms?.grantedBy && (
                <span className="text-muted-foreground ml-2">
                  {t('admin.permissions.last_modified_by', { name: userPerms.grantedBy })}
                </span>
              )}
            </div>

            {isLoadingPerms ? (
              <div className="text-sm text-muted-foreground">{t('admin.permissions.loading_permissions')}</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {TAB_OPTIONS(t).map((tab) => {
                  const checked = selectedTabs.includes(tab.id);
                  return (
                    <label
                      key={tab.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        checked ? 'border-primary bg-primary/5' : 'hover:bg-accent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTab(tab.id)}
                        className="mt-0.5 h-4 w-4 accent-primary"
                      />
                      <div>
                        <div className="text-sm font-medium">{t(`admin.permissions.${tab.id.replace(/[:-]/g, '_')}_label`)}</div>
                        <div className="text-xs text-muted-foreground">{t(`admin.permissions.${tab.id.replace(/[:-]/g, '_')}_desc`)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={setMutation.isPending}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {setMutation.isPending ? t('admin.permissions.saving') : t('admin.permissions.save_permissions_btn')}
            </button>
          </div>
        )}
      </div>

      {/* All users with extra permissions */}
      <div className="rounded-lg border bg-card p-6 space-y-4">
        <h2 className="font-semibold">{t('admin.permissions.users_with_extra_title')}</h2>
        {isLoadingAll ? (
          <div className="text-sm text-muted-foreground">{t('common.ui.loading')}</div>
        ) : !allUsers || allUsers.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t('admin.permissions.no_users_with_extra')}</div>
        ) : (
          <div className="space-y-3">
            {allUsers.map((item) => (
              <div
                key={item.email}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border p-3 text-sm cursor-pointer hover:bg-accent"
                onClick={() => {
                  setSearchEmail(item.email);
                  setQueriedEmail(item.email);
                  setSelectedTabs((item.tabs ?? []) as TabId[]);
                }}
              >
                <div>
                  <div className="font-medium">{item.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.tabs.length === 0
                      ? t('admin.permissions.no_extra_permissions')
                      : item.tabs.map((tabId) => t(`admin.permissions.${tabId.replace(/[:-]/g, '_')}_label`, { defaultValue: tabId })).join(', ')}
                  </div>
                </div>
                {item.grantedBy && (
                  <div className="text-xs text-muted-foreground shrink-0">{t('admin.permissions.by_who', { name: item.grantedBy })}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
